import * as React from 'react';
import {createAction} from '@packages/utilities/create-action';
import {emptyObject} from '@packages/utilities/fp/utils';
import {updateIfNeeded} from '@packages/utilities/POJORecordUtils';

const HITAREA_BLURRED = 'HITAREA_BLURRED' as const;
const HITAREA_FOCUSED = 'HITAREA_FOCUSED' as const;
const HITAREA_HOVERED = 'HITAREA_HOVERED' as const;
const HITAREA_KEY_PRESSED = 'HITAREA_KEY_PRESSED' as const;
const HITAREA_KEY_DEPRESSED = 'HITAREA_KEY_DEPRESSED' as const;
const HITAREA_MOUSE_FOCUSED = 'HITAREA_MOUSE_FOCUSED' as const;
const HITAREA_MOUSE_BLURRED = 'HITAREA_MOUSE_BLURRED' as const;
const HITAREA_UNHOVERED = 'HITAREA_UNHOVERED' as const;

const blurHitarea = createAction(HITAREA_BLURRED);
const focusHitarea = createAction(HITAREA_FOCUSED);
const hoverHitarea = createAction(HITAREA_HOVERED);
const keyDownHitarea = createAction(HITAREA_KEY_PRESSED);
const keyUpHitarea = createAction(HITAREA_KEY_DEPRESSED);
const mouseFocusHitarea = createAction(HITAREA_MOUSE_FOCUSED);
const mouseBlurHitarea = createAction(HITAREA_MOUSE_BLURRED);
const unHoverHitarea = createAction(HITAREA_UNHOVERED);

const ENTER = 13 as const;
const SPACE = 32 as const;

export type State = {
  focus?: boolean;
  keyboardFocus?: boolean;
  hover?: boolean;
  active?: boolean;
};

const focusState = {
  focus: true,
} as const;

const keyboardFocusState = {
  focus: true,
  keyboardFocus: true,
} as const;

const unfocusState = {
  focus: false,
  keyboardFocus: false,
} as const;

const hoverState = {
  hover: true,
} as const;

const unhoverState = {
  hover: false,
  active: false,
} as const;

const activeState = {
  active: true,
} as const;

const activeMouseFocusState = {
  active: true,
  keyboardFocus: false,
} as const;

const inactiveState = {
  active: false,
} as const;

// @ts-expect-error - TS7006 - Parameter 'action' implicitly has an 'any' type.
const reducer = (state: State, action): State => {
  switch (action.type) {
    case HITAREA_FOCUSED:
      return updateIfNeeded(
        state,
        state.active ? focusState : keyboardFocusState
      );

    case HITAREA_BLURRED:
      return updateIfNeeded(state, unfocusState);

    case HITAREA_HOVERED:
      return updateIfNeeded(state, hoverState);

    case HITAREA_UNHOVERED:
      return updateIfNeeded(state, unhoverState);

    case HITAREA_MOUSE_FOCUSED:
      return updateIfNeeded(state, activeMouseFocusState);

    case HITAREA_KEY_PRESSED:
      return updateIfNeeded(state, activeState);

    case HITAREA_MOUSE_BLURRED:
    case HITAREA_KEY_DEPRESSED:
      return updateIfNeeded(state, inactiveState);

    default:
      return state;
  }
};

export type Handlers = {
  onFocus?: (event?: React.FocusEvent<EventTarget>) => void;
  onBlur?: (event?: React.FocusEvent<EventTarget>) => void;
  onMouseEnter?: (event?: React.MouseEvent<EventTarget>) => void;
  onMouseOver?: (event?: React.MouseEvent<EventTarget>) => void;
  onMouseLeave?: (event?: React.MouseEvent<EventTarget>) => void;
  onMouseDown?: (event?: React.MouseEvent<EventTarget>) => void;
  onMouseUp?: (event?: React.MouseEvent<EventTarget>) => void;
  onKeyDown?: (event: React.KeyboardEvent<EventTarget>) => void;
  onKeyUp?: (event: React.KeyboardEvent<EventTarget>) => void;
};

type UseHitArea = (
  controlled: undefined | (State & Handlers)
) => [State, Handlers];

/**
 * An evolution from our Hitarea higher-order component, this custom hook
 * manages a component's focus and active states and provides the following
 * states:
 *
 * • If the current element has `focus` (eg input is focused)
 * • If the current element is `active` (eg button is "pressed")
 * • If the current element has `hover` (eg highlight background on hover)
 * • If the current element has `keyboardFocus` (eg input is focused)
 *
 * ### Usage
 *
 * Prior to this hook, the previous abstraction `Hitarea` was only able to
 * provide these states to a _single_ component that it wrapped:
 *
 * ```jsx
 * <Hitarea>
 *   <Button /> // `Hitarea` injects the states via props since it is a HOC
 * <Hitarea>
 * ```
 *
 * Now:
 *
 * ```jsx
 * import useHitarea from '@packages/systems/ui/hooks/useHitarea';
 *
 * const MyComponent = () => {
 *   const [hitareaState, hitareaHandlers] = useHitarea();
 *
 *   return (
 *     <>
 *       // Upgrade from Hitarea — now other components can respond to hitarea
 *       // state changes like so:
 *       <span>{hitareaState.focus ? 'I am focused!!!' : null}</span>
 *
 *       // Component that is activating the hitarea states
 *       <Button {...hitareaState} {...hitareaHandlers} />
 *
 *       <Checkbox active={hitareaState.active}/>
 *     </>
 *   )
 * }
 * ```
 *
 * By default `useHitarea`, works as a standalone hook and solves the majority
 * of use cases (components that only needs to manage hitarea state locally).
 * However, there will be instances where state needs to be controlled, such
 * as using and responding to state coming from flux. For these cases,
 * `useHitarea` accepts an optional argument that provides consumers fine-grain
 * control over the management of the hitarea states.
 *
 * See https://reactjs.org/docs/forms.html#controlled-components for more
 * details on the control vs uncontrolled pattern.
 *
 * ```jsx
 * import useHitarea from '@packages/systems/ui/hooks/useHitarea';
 *
 * const MyComponent = ({focusStyleRuleSelector, dispatch}) => {
 *   const [state, handlers] = useHitarea({
 *     focus: focusStyleRuleSelector,
 *     onBlur: () => dispatch({type: 'SELECTOR_BLURRED'}),
 *     onFocus: () => dispatch({type: 'SELECTOR_FOCUSED'})
 *   })
 *
 *   return (
 *     <>
 *       // `focus` is now managed externally instead of by the hook
 *       <span>{state.focus ? 'style rule focused!!!' : null}</span>
 *
 *       <Button {...state} {...handlers} />
 *     </>
 *   )
 * }
 * ```
 */
const useHitarea: UseHitArea = (controlled) => {
  const {
    focus,
    keyboardFocus,
    hover,
    active,

    onFocus,
    onBlur,
    onMouseEnter,
    onMouseOver,
    onMouseLeave,
    onMouseDown,
    onMouseUp,
    onKeyDown,
    onKeyUp,
  } = controlled || emptyObject;

  const initialState: State = {
    focus: Boolean(focus),
    keyboardFocus: Boolean(keyboardFocus),
    hover: Boolean(hover),
    active: Boolean(active),
  };

  const [state, dispatch] = React.useReducer(reducer, initialState);

  const handlers: Handlers = React.useMemo(
    () => ({
      onFocus: (event) => {
        onFocus && onFocus(event);
        dispatch(focusHitarea());
      },
      onBlur: (event) => {
        onBlur && onBlur(event);
        dispatch(blurHitarea());
      },
      onMouseEnter: (event) => {
        onMouseEnter && onMouseEnter(event);
        dispatch(hoverHitarea());
      },
      onMouseOver: (event) => {
        onMouseOver && onMouseOver(event);
        dispatch(hoverHitarea());
      },
      onMouseLeave: (event) => {
        onMouseLeave && onMouseLeave(event);
        dispatch(unHoverHitarea());
      },
      onMouseDown: (event) => {
        onMouseDown && onMouseDown(event);
        dispatch(mouseFocusHitarea());
      },
      onMouseUp: (event) => {
        onMouseUp && onMouseUp(event);
        dispatch(mouseBlurHitarea());
      },
      onKeyDown: (event) => {
        onKeyDown && onKeyDown(event);

        const keyCode = event.which;
        if (keyCode === ENTER || keyCode === SPACE) {
          dispatch(keyDownHitarea());
        }
      },
      onKeyUp: (event) => {
        onKeyUp && onKeyUp(event);

        const keyCode = event.which;
        if (keyCode === ENTER || keyCode === SPACE) {
          dispatch(keyUpHitarea());
        }
      },
    }),
    [
      onFocus,
      onBlur,
      onMouseEnter,
      onMouseOver,
      onMouseLeave,
      onMouseDown,
      onMouseUp,
      onKeyDown,
      onKeyUp,
    ]
  );

  // prettier-ignore
  const mergedState = React.useMemo(
    () => ({
      focus: focus !== undefined ? focus : state.focus,
      keyboardFocus: keyboardFocus !== undefined ? keyboardFocus : state.keyboardFocus,
      hover: hover !== undefined ? hover : state.hover,
      active: active !== undefined ? active : state.active,
    }),
    [focus, keyboardFocus, hover, active, state]
  );

  return [mergedState, handlers];
};

export default useHitarea;
